import { useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { isNil } from 'lodash'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TableContainer,
  TextField,
  Typography,
} from '@material-ui/core'
import { UploadCloud as UploadCloudIcon } from 'react-feather'

import { MenuButton } from 'components'
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from 'components/Table'

import ZitadelVerifyStatus from '../../../ZitadelVerifyStatus'
import useStyles from './styles'

const METADATA_INPUT_TYPES = [
  { id: 'url', label: 'URL' },
  { id: 'xml_file', label: 'Arquivo XML' },
  {
    id: 'xml_content',
    label: 'Conteúdo do Metadados (Deve ser um XML válido)',
  },
]

const MetadataInput = ({
  control,
  watch,
  errors,
  disabled,
  setMetadataXmlFile,
  ssoCorporateGroup,
  statusVerified,
  setStatusVerified,
  refresh,
}) => {
  const [xmlFile, setXmlFile] = useState(null)
  const refInput = useRef(null)
  const classes = useStyles()

  const metadataInputType = watch('metadataInputType')

  const handleFileChange = (event) => {
    const file = event.target.files[0] || null
    setXmlFile(file)
    setMetadataXmlFile(file)
  }

  const handleRemove = () => {
    if (refInput.current) refInput.current.value = ''
    setXmlFile(null)
    setMetadataXmlFile(null)
  }

  return (
    <Grid item xs={12}>
      <Box mt={1}>
        <Typography gutterBottom variant="subtitle2" color="textPrimary">
          Metadados do provedor de identidade
        </Typography>
      </Box>
      <FormControl component="fieldset">
        <Controller
          control={control}
          name="metadataInputType"
          as={
            <RadioGroup row>
              {METADATA_INPUT_TYPES.map((type) => (
                <FormControlLabel
                  key={type.id}
                  value={type.id}
                  control={<Radio size="small" color="primary" />}
                  label={type.label}
                  disabled={disabled}
                />
              ))}
            </RadioGroup>
          }
        />
      </FormControl>

      {metadataInputType === 'url' && (
        <Box mt={1}>
          <Controller
            control={control}
            name="metadataUrl"
            mode="onChange"
            as={
              <TextField
                fullWidth
                label="URL de metadados do provedor de identidade"
                error={!!errors.metadataUrl}
                helperText={errors?.metadataUrl?.message}
                disabled={disabled}
              />
            }
          />
          {!disabled && !!ssoCorporateGroup?.metadataUrl && (
            <ZitadelVerifyStatus
              zitadelIdpActivated={ssoCorporateGroup?.zitadelIdpActivated}
              statusVerified={statusVerified}
              setStatusVerified={setStatusVerified}
              ssoCorporateGroup={ssoCorporateGroup}
              refresh={refresh}
            />
          )}
        </Box>
      )}

      {metadataInputType === 'xml_file' && (
        <Box my={1}>
          <Button
            disabled={!isNil(xmlFile) || disabled}
            variant="contained"
            component="label"
            size="small"
            className={clsx(classes.uploadButton, {
              [classes.uploadButtonDisabled]: !isNil(xmlFile),
            })}
          >
            <Box pl={1} pt={1}>
              <UploadCloudIcon size="16px" />
            </Box>
            <Box px={1}>
              <Typography variant="h6">UPLOAD</Typography>
            </Box>
            <input
              type="file"
              ref={refInput}
              id="metadataXmlFile"
              accept="application/xml,text/xml,.xml"
              className={classes.input}
              onChange={handleFileChange}
            />
          </Button>
          {!isNil(xmlFile) && (
            <TableContainer className={classes.fileTable}>
              <Table
                size="small"
                emptyMessage="Nenhum arquivo encontrado"
                noWrap
              >
                <TableHead>
                  <TableRow>
                    <TableCell width="80%">Arquivo</TableCell>
                    <TableCell width="20%" align="right">
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow key={1}>
                    <TableCell disableTooltip>{xmlFile.name}</TableCell>
                    <TableCell disableTooltip align="right">
                      <Box display="flex" justifyContent="flex-end">
                        <MenuButton>
                          <Button
                            color="secondary"
                            fullWidth
                            onClick={handleRemove}
                          >
                            Remover
                          </Button>
                        </MenuButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {metadataInputType === 'xml_content' && (
        <Box mt={1}>
          <Controller
            control={control}
            name="metadataXml"
            mode="onBlur"
            as={
              <TextField
                fullWidth
                multiline
                minRows={6}
                error={!!errors.metadataXml}
                helperText={errors?.metadataXml?.message}
                label="Conteúdo do Metadados"
                placeholder="Cole o conteúdo XML aqui"
                disabled={disabled}
              />
            }
          />
        </Box>
      )}
    </Grid>
  )
}

MetadataInput.propTypes = {
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  setMetadataXmlFile: PropTypes.func.isRequired,
  ssoCorporateGroup: PropTypes.object,
  statusVerified: PropTypes.bool,
  setStatusVerified: PropTypes.func,
  refresh: PropTypes.func,
}

export default MetadataInput
